/**
 * Generic MIME-type/extension -> friendly category mapping, shared across
 * every module that displays an attachment to a non-technical user.
 *
 * Created for the Proposals attachment-display audit: attachments were
 * showing a raw technical MIME type (e.g.
 * "application/vnd.openxmlformats-officedocument.wordprocessingml.document")
 * instead of something a normal user recognizes. Rather than adding a
 * one-off mapping inside Proposals, this lives in `lib/` so any other
 * module (Drive, Bills, Invoices, Change Orders, ...) can reuse the exact
 * same categories instead of maintaining its own parallel map.
 *
 * `src/features/drive/components/DriveItem.tsx` still has its own local
 * `getFileIcon()` (a different concern: picking a Lucide icon component,
 * not a label) -- not touched here since Drive is outside this audit's
 * scope, but it's a natural candidate to adopt `getFileCategory` too in a
 * future pass so the mapping itself isn't duplicated.
 */

export type FileCategory =
  | 'image'
  | 'pdf'
  | 'word'
  | 'excel'
  | 'powerpoint'
  | 'archive'
  | 'video'
  | 'audio'
  | 'text'
  | 'other';

const CATEGORY_LABELS: Record<FileCategory, string> = {
  image: 'Image',
  pdf: 'PDF',
  word: 'Word',
  excel: 'Excel',
  powerpoint: 'PowerPoint',
  archive: 'Archive',
  video: 'Video',
  audio: 'Audio',
  text: 'Text',
  other: 'Other',
};

/**
 * Classifies a file into a small, human-friendly category. Prefers the
 * MIME type (authoritative); falls back to the file extension when the
 * MIME type is missing or generic (e.g. `application/octet-stream`).
 */
export function getFileCategory(mimeType?: string | null, fileName?: string | null): FileCategory {
  const mime = (mimeType || '').toLowerCase();
  const ext = (fileName || '').split('.').pop()?.toLowerCase() || '';

  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime === 'application/pdf') return 'pdf';
  if (
    mime === 'application/msword' ||
    mime.includes('wordprocessingml') ||
    ['doc', 'docx', 'dotx', 'rtf'].includes(ext)
  ) {
    return 'word';
  }
  if (
    mime === 'application/vnd.ms-excel' ||
    mime.includes('spreadsheetml') ||
    ['xls', 'xlsx', 'csv'].includes(ext)
  ) {
    return 'excel';
  }
  if (
    mime === 'application/vnd.ms-powerpoint' ||
    mime.includes('presentationml') ||
    ['ppt', 'pptx'].includes(ext)
  ) {
    return 'powerpoint';
  }
  if (
    mime === 'application/zip' ||
    mime === 'application/x-rar' ||
    mime.includes('7z') ||
    ['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)
  ) {
    return 'archive';
  }
  if (mime.startsWith('text/') && !mime.includes('html')) return 'text';

  return 'other';
}

/** Short, user-facing label for a file's category (e.g. "Word", not the raw MIME type). */
export function getFileCategoryLabel(mimeType?: string | null, fileName?: string | null): string {
  return CATEGORY_LABELS[getFileCategory(mimeType, fileName)];
}
